/* eslint-disable no-undef */
/*global google*/

// import { withScriptjs, withGoogleMap, GoogleMap, Marker, GroundOverlay } from "react-google-maps";
import {
  Circle,
  GoogleMap,
  GroundOverlay,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { getBoundsForMarina } from '../actions/moorings';

export const GOOGLE_MAPS_LIBRARIES = ['places'];

// google.maps can exist as a bootstrap stub before Map is available.
// Treating that stub as "loaded" causes: "google.maps.Map is not a constructor".
export const isGoogleMapsApiReady = () =>
  typeof window !== 'undefined' &&
  typeof window.google?.maps?.Map === 'function';

const greenPin = {
  url: 'https://mt.google.com/vt/icon?psize=30&font=fonts/arialuni_t.ttf&color=ff304C13&name=icons/spotlight/spotlight-waypoint-a.png&ax=43&ay=48&text=%E2%80%A2', // url
};
const redPin = {
  url: 'https://mt.googleapis.com/vt/icon/name=icons/spotlight/spotlight-poi.png&scale=1', // url
};
const bluePin = {
  url: 'https://mt.google.com/vt/icon?psize=20&font=fonts/Roboto-Regular.ttf&color=ff330000&name=icons/spotlight/spotlight-waypoint-blue.png&ax=44&ay=48&scale=1&text=%E2%80%A2', // url
};
const whitePin = {
  url: 'https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_white.png',
};
const greyPin = {
  url: 'https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_grey.png',
};
const greenPinC = {
  url: 'https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_greenC.png',
};
const redPinC = {
  url: 'https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_redC.png',
};
const bluePinC = {
  url: 'https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_blueC.png',
};

const specialPin = {
  // url: 'https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_blackA.png'
  url: 'https://mt.google.com/vt/icon/name=icons/spotlight/spotlight-ad.png',
  // url: 'https://mt.google.com/vt/icon?psize=20&font=fonts/Roboto-Regular.ttf&color=ff330000&name=icons/spotlight/spotlight-waypoint-blue.png&ax=44&ay=48&scale=1&text=D', // url
};

const getPinIcon = (mooring, forceGreenPins = false) => {
  if (forceGreenPins) return greenPin;
  var icon = redPin;

  if (mooring.app_visible == 1) {
    // mooring oublically visible
    if (mooring.availability_status == 'available') icon = greenPin;
    if (mooring.availability_status == 'partially_available') icon = bluePin;
    if (mooring.availability_status == 'available_day_by_day') icon = bluePin;
    if (mooring.availability_status == 'unavailable') icon = redPin;
    if (mooring.availability_status == 'not_released') icon = greyPin;

    if (mooring.claimable) {
      if (mooring.availability_status == 'available') icon = greenPinC;
      if (mooring.availability_status == 'partially_available') icon = bluePinC;
      if (mooring.availability_status == 'available_day_by_day')
        icon = bluePinC;
      if (mooring.availability_status == 'unavailable') icon = redPinC;
    }
  } else {
    // Mooring not app visible
    icon = mooring.rent ? whitePin : specialPin;
  }

  return icon;
};

export function MooringsMap(props) {
  const {
    containerStyle: containerStyleProp,
    mapOptions: mapOptionsProp,
    center,
    zoom,
    onMapReady,
    userLocation,
    pois = [],
    onPoiClick,
    mooringLabelModulo: mooringLabelModuloProp,
    mooringLabelZoom: mooringLabelZoomProp,
    fetchBoundsForMarina: fetchBoundsForMarinaProp,
    setBounds,
  } = props;
  const { isLoaded: hookLoaded } = useJsApiLoader({
    id: 'google-map-script',
    libraries: GOOGLE_MAPS_LIBRARIES,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });
  const isLoaded = hookLoaded || isGoogleMapsApiReady();

  const [map, setMap] = useState(null);
  const [windowSize, setWindowSize] = useState([
    window.innerWidth,
    window.innerHeight,
  ]);
  const [blueDot, setBlueDot] = useState(null);
  const mooringLabelModulo = mooringLabelModuloProp ?? 5;
  const mooringLabelZoomThreshold = mooringLabelZoomProp ?? 20;
  const defaultPoiIcon = useMemo(() => {
    if (!isLoaded || !window.google?.maps?.SymbolPath) return null;
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: '#ff8a00',
      fillOpacity: 1,
      scale: 8,
      strokeColor: '#ffffff',
      strokeWeight: 2,
    };
  }, [isLoaded]);
  const previousCenterRef = useRef(null);
  const previousZoomRef = useRef(null);
  const boundsUpdateTimeoutRef = useRef(null);

  useEffect(() => {
    const handleWindowResize = () => {
      setWindowSize([window.innerWidth, window.innerHeight]);
    };
    window.addEventListener('resize', handleWindowResize);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      // Clean up timeout on unmount
      if (boundsUpdateTimeoutRef.current) {
        clearTimeout(boundsUpdateTimeoutRef.current);
      }
    };
  }, [onMapReady]);

  const lastReportedBoundsRef = useRef(null);

  const reportMapBounds = React.useCallback(
    (mapInstance) => {
      if (typeof setBounds !== 'function') return;
      const mapBounds = mapInstance.getBounds();
      if (!mapBounds) return;
      const northEast = mapBounds.getNorthEast();
      const southWest = mapBounds.getSouthWest();
      const bounds = {
        min_lng: southWest.lng(),
        min_lat: southWest.lat(),
        max_lng: northEast.lng(),
        max_lat: northEast.lat(),
      };
      const previous = lastReportedBoundsRef.current;
      const epsilon = 0.00005;
      if (
        previous &&
        Math.abs(previous.min_lat - bounds.min_lat) < epsilon &&
        Math.abs(previous.max_lat - bounds.max_lat) < epsilon &&
        Math.abs(previous.min_lng - bounds.min_lng) < epsilon &&
        Math.abs(previous.max_lng - bounds.max_lng) < epsilon
      ) {
        return;
      }
      lastReportedBoundsRef.current = bounds;
      setBounds(bounds);
    },
    [setBounds],
  );

  const onLoad = React.useCallback(
    (mapInstance) => {
      const hasInitialView =
        center &&
        Number.isFinite(center.lat) &&
        Number.isFinite(center.lng) &&
        zoom != null;

      if (hasInitialView) {
        mapInstance.setCenter(center);
        mapInstance.setZoom(zoom);
        previousCenterRef.current = center;
        previousZoomRef.current = zoom;
        window.google?.maps?.event?.addListenerOnce(mapInstance, 'idle', () => {
          reportMapBounds(mapInstance);
        });
      } else {
        const boundsFetcher =
          typeof fetchBoundsForMarinaProp === 'function'
            ? fetchBoundsForMarinaProp
            : getBoundsForMarina;
        boundsFetcher(
          (marina_bounds) => {
            var { min_lat, min_lng, max_lat, max_lng } = marina_bounds;

            var bounds = new window.google.maps.LatLngBounds();
            var min = new window.google.maps.LatLng(min_lat, min_lng);
            var max = new window.google.maps.LatLng(max_lat, max_lng);
            bounds.extend(min);
            bounds.extend(max);
            mapInstance.fitBounds(bounds);
          },
          () => {},
        );
      }

      if (window.google?.maps?.SymbolPath) {
        const dot = {
          fillColor: '#4285F4',
          fillOpacity: 1,
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          strokeColor: 'rgb(255,255,255)',
          strokeWeight: 2,
        };
        setBlueDot(dot);
      }

      setMap(mapInstance);
      if (typeof onMapReady === 'function') {
        onMapReady(mapInstance);
      }
    },
    [center, fetchBoundsForMarinaProp, onMapReady, reportMapBounds, zoom],
  );

  const getPoiMarkerIcon = React.useCallback(
    (poi) => {
      if (poi?.icon_url && window.google?.maps) {
        return {
          url: poi.icon_url,
          scaledSize: new window.google.maps.Size(28, 28),
        };
      }
      return defaultPoiIcon || undefined;
    },
    [defaultPoiIcon],
  );

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);

  useEffect(() => {
    if (!map || !center) return;
    const previousCenter = previousCenterRef.current;
    const latChanged =
      !previousCenter || Math.abs(previousCenter.lat - center.lat) > 0.000001;
    const lngChanged =
      !previousCenter || Math.abs(previousCenter.lng - center.lng) > 0.000001;
    if (latChanged || lngChanged) {
      map.panTo(center);
      previousCenterRef.current = center;
    }
  }, [map, center]);

  useEffect(() => {
    if (!map || zoom == null) return;
    const previousZoom = previousZoomRef.current;
    if (previousZoom !== zoom) {
      map.setZoom(zoom);
      previousZoomRef.current = zoom;
    }
  }, [map, zoom]);

  const onMapIdle = React.useCallback(() => {
    if (!map || !isLoaded) return;

    if (boundsUpdateTimeoutRef.current) {
      clearTimeout(boundsUpdateTimeoutRef.current);
    }

    boundsUpdateTimeoutRef.current = setTimeout(() => {
      if (!map || !isLoaded) return;
      reportMapBounds(map);
    }, 700);
  }, [isLoaded, map, reportMapBounds]);

  const googleMapOptions = {
    streetViewControl: false,
    draggable: true, // make map draggable
    zoomControlOptions: { position: 9 },
    keyboardShortcuts: false, // disable keyboard shortcuts
    scaleControl: true, // allow scale controle
    scrollwheel: true, // allow scroll wheel
    mapTypeId: 'hybrid', // 'hybrid' shows satellite imagery with labels (city names, roads, etc.)
    ...mapOptionsProp,
    minZoom: 3.5, // prevent zooming out beyond the whole world
    maxZoom: 21, // maximum zoom level
  };

  const containerStyle = containerStyleProp || {
    width: '100%',
    height: windowSize[1] - 289,
  };

  const currentZoom =
    typeof map?.getZoom === 'function' ? map.getZoom() : map?.zoom;
  const { allMoorings, clusters, filter, add_mooring_position, map_overlays } =
    props;
  const forceGreenPins = Boolean(props.forceGreenPins);
  // console.log(allMoorings[20].rent);
  return isLoaded ? (
    <div id='google_map_container' style={{ flex: 1 }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={googleMapOptions}
        tilt={0}
        onClick={props.onClick}
        onIdle={onMapIdle}
        minZoom={0.5}>
        {/* Child components, such as markers, info windows, etc. */}
        <></>

        {Array.isArray(clusters) &&
          clusters.map((cluster) => (
            <Marker
              key={`cluster_${cluster.latitude}_${cluster.longitude}`}
              position={{
                lat: parseFloat(cluster.latitude),
                lng: parseFloat(cluster.longitude),
              }}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                fillColor: '#003066',
                fillOpacity: 1,
                scale: 10 + Math.min(cluster.size * 0.5, 20),
                strokeColor: '#003c7d',
                strokeWeight: 5,
              }}
              label={{
                text: cluster.size.toString(),
                color: 'white',
                fontSize:
                  Math.max(12, 10 + Math.min(cluster.size * 0.5, 10)) + 'px',
                fontWeight: 'bold',
              }}
              onClick={() => {
                if (!map) return;
                map.panTo({
                  lat: parseFloat(cluster.latitude),
                  lng: parseFloat(cluster.longitude),
                });
                map.setZoom(Math.min(map.getZoom() + 2, 21));
              }}
              zIndex={google.maps.Marker.MAX_ZINDEX + cluster.size}
              optimized={false}
            />
          ))}

        {Array.isArray(pois) &&
          pois.map((poi) => {
            const lat = Number(poi.latitude);
            const lng = Number(poi.longitude);
            if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
              return null;
            }
            return (
              <Marker
                key={`poi_${poi.id}_${lat}_${lng}`}
                position={{
                  lat,
                  lng,
                }}
                icon={getPoiMarkerIcon(poi)}
                label={
                  poi?.icon_url
                    ? undefined
                    : {
                      text: 'i',
                      color: '#ffffff',
                      fontWeight: 'bold',
                      fontSize: '12px',
                    }
                }
                onClick={() => {
                  if (typeof onPoiClick === 'function') {
                    onPoiClick(poi);
                  }
                }}
                zIndex={100}
              />
            );
          })}

        {Array.isArray(allMoorings) &&
          allMoorings.map((mooring) => (
            <Marker
              label={
                filter.show_berth_numbers
                  ? (mooringLabelModulo === 0 ||
                    mooring.mooring_id % mooringLabelModulo === 0) &&
                    currentZoom >= mooringLabelZoomThreshold
                    ? {
                      className: 'labelClass',
                      color: '#00aaff',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      text: mooring.berth_number,
                    }
                    : ''
                  : ''
              }
              title={
                mooring.berth_number ? String(mooring.berth_number) : undefined
              }
              key={mooring.mooring_id}
              position={{
                lat: parseFloat(mooring.latitude),
                lng: parseFloat(mooring.longitude),
              }}
              icon={getPinIcon(mooring, forceGreenPins)}
              onClick={() => props.onPinClick(mooring)}
              zIndex={400}
            />
          ))}

        {filter.show_map_overlays &&
          map_overlays
            .filter((overlay) => overlay.account_id !== null)
            .map((map_overlay, index) => (
              <GroundOverlay
                key={`private_overlay_${index}`}
                bounds={{
                  north: Math.max(map_overlay.lat1, map_overlay.lat2),
                  south: Math.min(map_overlay.lat1, map_overlay.lat2),
                  east: Math.max(map_overlay.lng1, map_overlay.lng2),
                  west: Math.min(map_overlay.lng1, map_overlay.lng2),
                }}
                url={map_overlay.url}
              />
            ))}
        {map_overlays
          .filter((overlay) => overlay.account_id == null)
          .map((map_overlay, index) => (
            <GroundOverlay
              key={index}
              bounds={{
                north: Math.max(map_overlay.lat1, map_overlay.lat2),
                south: Math.min(map_overlay.lat1, map_overlay.lat2),
                east: Math.max(map_overlay.lng1, map_overlay.lng2),
                west: Math.min(map_overlay.lng1, map_overlay.lng2),
              }}
              url={map_overlay.url}
            />
          ))}

        {add_mooring_position && (
          <Marker
            key={0}
            position={{
              lat: parseFloat(add_mooring_position.latitude),
              lng: parseFloat(add_mooring_position.longitude),
            }}
            icon={redPin}
          />
        )}
        {userLocation && blueDot && (
          <>
            <Marker
              key='user_location'
              position={{
                lat: parseFloat(userLocation.latitude),
                lng: parseFloat(userLocation.longitude),
              }}
              icon={blueDot}
            />
            {userLocation.accuracy && (
              <Circle
                center={{
                  lat: parseFloat(userLocation.latitude),
                  lng: parseFloat(userLocation.longitude),
                }}
                radius={userLocation.accuracy}
                options={{
                  fillColor: '#61a0bf',
                  strokeColor: '#4285F4',
                  strokeOpacity: 0.4,
                  strokeWeight: 1,
                  zIndex: 1,
                }}
              />
            )}
          </>
        )}
      </GoogleMap>
    </div>
  ) : (
    <></>
  );
}
